import type { QueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import type { Notification, PaginatedResponse } from '@/types';

const NOTIFICATIONS_LIST_KEY: [string, { limit: number }] = ['notifications', { limit: 50 }];
const NOTIFICATIONS_BELL_KEY: [string, { limit: number }] = ['notifications', { limit: 10 }];

function patchList(
  queryClient: QueryClient,
  queryKey: [string, { limit: number }],
  updater: (old: PaginatedResponse<Notification> | undefined) => PaginatedResponse<Notification> | undefined
) {
  queryClient.setQueryData<PaginatedResponse<Notification>>(queryKey, (old) => updater(old) ?? old);
}

function trimToLimit(page: PaginatedResponse<Notification>, limit: number): PaginatedResponse<Notification> {
  if (page.data.length <= limit) return page;
  return {
    ...page,
    data: page.data.slice(0, limit),
    meta: { ...page.meta, limit },
  };
}

/**
 * Keeps React Query notification caches in sync with Socket.IO events.
 * Registered once on the shared notifications socket (see SocketProvider).
 */
export function subscribeNotificationSocketQuerySync(socket: Socket, queryClient: QueryClient): () => void {
  const handleNewNotification = (notification: Notification) => {
    for (const key of [NOTIFICATIONS_LIST_KEY, NOTIFICATIONS_BELL_KEY] as const) {
      const limit = key[1].limit;
      patchList(queryClient, key, (old) => {
        if (!old) {
          return trimToLimit(
            {
              data: [notification],
              meta: { total: 1, limit },
            },
            limit
          );
        }
        if (old.data.some((n) => n.id === notification.id)) return old;
        return trimToLimit(
          {
            ...old,
            data: [notification, ...old.data],
            meta: { ...old.meta, total: old.meta.total + 1, limit },
          },
          limit
        );
      });
    }

    if (!notification.isRead) {
      queryClient.setQueryData<number>(['notifications', 'unread-count'], (old) =>
        typeof old === 'number' ? old + 1 : 1
      );
    }
  };

  const handleNotificationRead = (data: { id: string }) => {
    for (const key of [NOTIFICATIONS_LIST_KEY, NOTIFICATIONS_BELL_KEY] as const) {
      patchList(queryClient, key, (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((n) => (n.id === data.id ? { ...n, isRead: true } : n)),
        };
      });
    }

    queryClient.setQueryData<number>(['notifications', 'unread-count'], (old) =>
      typeof old === 'number' && old > 0 ? old - 1 : 0
    );
  };

  const handleAllRead = () => {
    for (const key of [NOTIFICATIONS_LIST_KEY, NOTIFICATIONS_BELL_KEY] as const) {
      patchList(queryClient, key, (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((n) => ({ ...n, isRead: true })),
        };
      });
    }
    queryClient.setQueryData(['notifications', 'unread-count'], 0);
  };

  socket.on('notification:new', handleNewNotification);
  socket.on('notification:read', handleNotificationRead);
  socket.on('notification:all-read', handleAllRead);

  return () => {
    socket.off('notification:new', handleNewNotification);
    socket.off('notification:read', handleNotificationRead);
    socket.off('notification:all-read', handleAllRead);
  };
}
